import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { setRoleOverrides } from "@/pages/navConfigs";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // If user has a custom (admin-created) role, this holds the role doc
  // including the list of features the admin enabled for it.
  const [customRole, setCustomRole] = useState(null);
  const [, setNavVersion] = useState(0);

  const fetchCustomRole = useCallback(async (roleKey) => {
    if (roleKey) {
      // Role overrides drive the sidebar — load them on every login, not only on page refresh.
      try { setRoleOverrides((await api.get("/admin/role-overrides")).data || {}); setNavVersion((v) => v + 1); } catch { /* non-fatal */ }
    }
    if (!roleKey || BUILTIN_ROLE_KEYS.has(roleKey)) {
      setCustomRole(null);
      return null;
    }
    try {
      const r = await api.get("/admin/custom-roles");
      const match = (r.data || []).find((c) => c.role_key === roleKey);
      setCustomRole(match || null);
      return match || null;
    } catch {
      setCustomRole(null);
      return null;
    }
  }, []);

  const refresh = useCallback(async () => {
    try {
      const r = await api.get("/auth/me");
      setUser(r.data);
      await fetchCustomRole(r.data?.role);
      // Load role overrides so sidebar reflects admin's per-role config
      try {
        const ovr = await api.get("/admin/role-overrides");
        setRoleOverrides(ovr.data || {});
      } catch { /* non-fatal */ }
    } catch {
      setUser(null);
      setCustomRole(null);
      localStorage.removeItem("munners_token");
    } finally {
      setLoading(false);
    }
  }, [fetchCustomRole]);

  // Safety net: whenever the user changes (e.g. set directly by Login.jsx
  // or AuthCallback) re-fetch the custom-role doc so the dashboard nav
  // always knows what features are enabled.
  useEffect(() => {
    fetchCustomRole(user?.role);
  }, [user?.role, fetchCustomRole]);

  useEffect(() => {
    // If returning from Emergent OAuth, AuthCallback handles it
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    refresh();
  }, [refresh]);

  const login = async (email, password) => {
    const r = await api.post("/auth/login", { email, password });
    localStorage.setItem("munners_token", r.data.token);
    setUser(r.data.user);
    await fetchCustomRole(r.data.user?.role);
    return r.data;
  };

  const exchangeGoogle = async (sessionId) => {
    const r = await api.post("/auth/google/exchange", { session_id: sessionId });
    localStorage.setItem("munners_token", r.data.token);
    setUser(r.data.user);
    await fetchCustomRole(r.data.user?.role);
    return r.data;
  };

  const loginWithGoogleIdToken = async (credential) => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const r = await api.post("/auth/google/id-token", { credential });
    localStorage.setItem("munners_token", r.data.token);
    setUser(r.data.user);
    await fetchCustomRole(r.data.user?.role);
    return r.data;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    localStorage.removeItem("munners_token");
    setUser(null);
    setCustomRole(null);
  };

  return (
    <AuthCtx.Provider value={{ user, loading, login, logout, refresh, exchangeGoogle, loginWithGoogleIdToken, setUser, customRole }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);

export const ROLE_LABELS = {
  admin: "Admin",
  regional_pm: "Regional PM",
  project_manager: "Project Manager",
  social_media_manager: "Social Media Manager",
  social_media_executive: "Social Media Executive",
  head_media_comm: "Head of Media & Comm.",
  media_comm_executive: "M&C Executive",
  head_academics: "Head of Academics & Research",
  content_writer: "Content Writer", // legacy alias for safety
  school_principal: "School Principal",
  seo: "SEO",
  designer: "Designer",
  zonal_pm: "Zonal Project Manager",
  accountant: "Accountant",
};

// Built-in role keys. Anything NOT in here is a custom (admin-created) role
// and should render with a distinguishing "Custom" sub-badge.
export const BUILTIN_ROLE_KEYS = new Set(Object.keys(ROLE_LABELS));
export const isCustomRole = (role) => !!role && !BUILTIN_ROLE_KEYS.has(role);

export const ROLE_COLORS = {
  admin: "bg-indigo-100 text-indigo-700",
  regional_pm: "bg-teal-100 text-teal-700",
  project_manager: "bg-orange-100 text-orange-700",
  social_media_manager: "bg-pink-100 text-pink-700",
  social_media_executive: "bg-pink-50 text-pink-600",
  head_media_comm: "bg-amber-100 text-amber-700",
  media_comm_executive: "bg-amber-50 text-amber-600",
  head_academics: "bg-cyan-100 text-cyan-700",
  content_writer: "bg-amber-100 text-amber-700",
  school_principal: "bg-violet-100 text-violet-700",
  seo: "bg-lime-100 text-lime-700",
  designer: "bg-fuchsia-100 text-fuchsia-700",
  zonal_pm: "bg-emerald-100 text-emerald-700",
  accountant: "bg-emerald-100 text-emerald-700",
};
